import { formatEventDate, formatEventTime } from 'utils/eventContract'
import {useState,useRef} from 'react'
import styled from 'styled-components'
import useToggleDropdown from '../../hooks/useToggleDropdown';
import { useNavigate } from 'react-router-dom';
import { deleteEvent, resetEventDeleteState, setViewEventModalEdit, setSinglePageEventData } from '../../features/events/eventSlice';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect } from 'react';
import { BsThreeDots } from 'react-icons/bs'  
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit'; 
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import useImageLoader from 'hooks/useImageLoader';
import {  DotsButton, DotsWrap, DotEle } from 'components/dots/dots.styled'
import DestructiveConfirmModal from 'components/modals/DestructiveConfirmModal'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import { useAppLanguage } from 'i18n/useAppLanguage'

export default function EventItem(props) {
    const { t } = useTranslation(['events', 'common'])
    const { currentLocale } = useAppLanguage()
    const navigate = useNavigate()  
    const { profileChurch } = useSelector((state) => state.profileChurch)
    const { admin } = useSelector((state) => state.auth)

    
    const [isEventHovered, setIsEventHovered] = useState(false)
    const dotsRef = useRef(null)
    const [isDotsDropped, setIsDotsDropped] = useToggleDropdown(dotsRef)

    const handleDotsDropdown = (e) => {
        e.stopPropagation()
        setIsDotsDropped.toggle(dotsRef) 
    }
 
    const dispatch = useDispatch()
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
    const { user } = useSelector((state) => state.auth)
    const canManage = Boolean(
        (user?.isOrganization && String(user?._id) === String(profileChurch?._id))
        || admin?.[profileChurch?._id]
    )
    const { isEventDeleted, isEventDeleteLoading, isEventDeleteError, eventDeleteMessage } = useSelector((state) => state.events)


    const [imageSource, { blur }] = useImageLoader(`${props.data.image.urlSmall}`, `${props.data.image.url}`)

    const handleDelete = (event) => {
        event.stopPropagation()
        dispatch(resetEventDeleteState())
        setIsDotsDropped.off()
        setConfirmDeleteOpen(true)
    }

    const handleConfirmDelete = () => dispatch(deleteEvent({
        index: props.index,
        eventId: props.data._id,
        churchId: props.data.churchId,
    }))

    useEffect(() => {
        if(isEventDeleted){
            setConfirmDeleteOpen(false)
        }
    }, [isEventDeleted])

    useEffect(() => {
        if(confirmDeleteOpen && isEventDeleteError && eventDeleteMessage){
            toast.error(eventDeleteMessage)
        }
    }, [confirmDeleteOpen, eventDeleteMessage, isEventDeleteError])

 
    const handleEdit = () => { 
        dispatch(setViewEventModalEdit({
                index: props.index,
                data: props.data
        }))
    } 
 
    const handleEventClick = () => {  
        if(isDotsDropped){
            return
        }
        dispatch(setSinglePageEventData({
            eventData: props.data,

            churchInfo: {
                _id: props.data.churchId,
                fullName: profileChurch.fullName,
                profileImg: profileChurch.profileImg
            }
        }))
 
        navigate(`/events/${props.data._id}`)
    }

    const handleEventHover = (hover) => {
        if(hover === true && props.data.discussionEnabled){
            setIsEventHovered(hover)
        }else if(hover === false && isEventHovered === true ){
            setIsEventHovered(false)
        }else{

            // do nothing, no need to rerender anything

        }
    }
 
    return (
        <>
          <EventItemContainer
            onMouseEnter={() => handleEventHover(true)}
            onMouseLeave={() => handleEventHover(false)}
            isHovered = {isEventHovered}
            onClick={handleEventClick}
            > 
            <EventImageWrap> 

                <EventImage 
                    onContextMenu={(e) => e.preventDefault()}
                    isHovered = {isEventHovered}
                    src={imageSource}
                    alt={t('events:singular')}
                    style={{
                        filter: blur ? 'blur(2px)' : 'none',
                        transition: 'all 2s ease-in-out',
                    }}
                />

                {canManage && (

                    <>
                        <DotsButton
                            ref={dotsRef}
                            onClick={handleDotsDropdown}
                            ><BsThreeDots />
                        </DotsButton>
                        {
                            isDotsDropped && (
                                <DotsWrap
                                    top='25px'
                                    right='15px'
                                    >
                                    {canManage && (
                                        <>
                                            <DotEle style={{color: 'red'}} onClick={handleDelete}> <DeleteOutlineIcon style={{marginRight: '10px'}} /> {t('common:delete')}</DotEle>
                                            <DotEle onClick={handleEdit}> <EditIcon style={{marginRight: '10px'}} /> {t('common:edit')}</DotEle>
                                        </>
                                    )} 
                                </DotsWrap>
                            )
                        }
                    </>
                )}
                
            </EventImageWrap> 
            <EventItemWrap>
                <EventDate>
                    {formatEventDate(props.data.startDate, currentLocale, t('events:detail.dateToBeAnnounced'))}
                    {props.data.startTime ? ` · ${formatEventTime(props.data.startTime, currentLocale)}` : ''}
                </EventDate>
                <EventName>{props.data.eventName}</EventName>
                <EventMeta>
                    <MetaIcon><PlaceOutlinedIcon /></MetaIcon>
                    <MetaText>{props.data.eventFormat === 'online' ? t('events:create.online') : (props.data.locationAddress || t('events:detail.locationTba'))}</MetaText>
                </EventMeta>
            </EventItemWrap>
          </EventItemContainer>
          <DestructiveConfirmModal
            open={confirmDeleteOpen}
            onClose={() => setConfirmDeleteOpen(false)}
            onConfirm={handleConfirmDelete}
            loading={isEventDeleteLoading}
            title={t('events:delete.title')}
            description={t('events:delete.description')}
            />
        </>
    )
}

const EventItemContainer = styled.div`
    cursor: pointer;
    display: flex;
    flex: 0 0 calc(33.333% - 14px);
    max-width: calc(33.333% - 14px);
    min-width: 0;
    flex-direction: column;
    justify-content: space-between;
    padding: 0px;
    height: fit-content;
    align-self: flex-start;
    margin: 10px 10px 10px 0px;
    border-radius: 15px;
    width: calc(33.333% - 14px);
    transition: transform .15s ease, box-shadow .15s ease;
    overflow: hidden;
    background-color: white;
    border: 1px solid #dfe3e5;
    box-shadow: 0 2px 8px rgba(25, 38, 42, .07);
    margin-bottom: 20px;
    position: relative;
    &:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(25, 38, 42, .12); }
    @media (max-width: 768px) { 
        justify-self: center;
        align-self: center;
        padding: 0px;
        width: 100%; 
        margin: 0px;
        flex: unset;
        margin: 0px;
        max-width: unset;
        height: fit-content; 
        justify-content: flex-start; 
        margin-bottom: 20px;

    }
`

const EventImageWrap = styled.div`
    width: 100%;
    aspect-ratio: 16 / 9;
    height: auto;
    overflow: hidden;
    border-radius: 15px 15px 0 0;
    position: relative;
    //remove scroll bar
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none;  /* IE 10+ */
    &::-webkit-scrollbar { /* WebKit */
        width: 0;
        height: 0;
        background: transparent;

    }

    & img {
        width: 100%;
        height: 100%;
        border-radius: 15px 15px 0 0;
        object-fit: cover;
    }

    @media (max-width: 768px) {
        border-radius: 8px;
        align-self: center;
        width: 100%;
        height: fit-content;
        max-height:unset; 
    }

`

const EventImage = styled.img`
    transition: all 0.4s ease-in-out;
    opacity: ${props => props.isHovered ? '0.88' : '1'};
`

const EventItemWrap = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column; 
    height: 150px;
    padding: 14px;
    position: relative;

    @media (max-width: 768px) {
        padding: 14px;
        min-height: 150px;
        height: 150px;
    }
`
const EventDate = styled.p`
    margin: 0 0 5px;
    color: var(--themeColor);
    font-size: 11px;
    font-weight: 800;
    letter-spacing: .01em;
`

const EventName = styled.div`
    width: 100%;
    display: -webkit-box;
    overflow: hidden;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    min-height: 39px;
    margin: 0 0 11px;
    font-weight: 750;
    font-size: 15px;
    line-height: 1.3;
    text-align: left;
    color: #202b2f;
`
const EventMeta = styled.div`
    width: 100%;
    display: flex;
    align-items: flex-start;
    min-width: 0;
    gap: 9px;
`
const MetaIcon = styled.span`
    width: 30px; height: 30px; flex: 0 0 30px; display: grid; place-items: center; border-radius: 9px;
    background: rgba(0, 180, 204, .10); color: var(--themeColor); svg { font-size: 17px; }
`
const MetaText = styled.span`
    min-width: 0; overflow: hidden; padding-top: 1px; color: #536065; font-size: 11px; font-weight: 650;
    line-height: 1.35; text-overflow: ellipsis; white-space: nowrap;
`
 
